<template>
  <div>
    <Movementmenu />

    <v-container fluid class="pa-6" tag="section">
      <v-row class="mb-2">
        <v-col cols="12">
          <h2 class="text-h4 font-weight-bold mb-2 primary--text">
            <v-icon large color="primary" class="mr-2">mdi-chart-box</v-icon>
            สรุปผลการประมวลผลการย้าย
          </h2>
          <div class="subtitle-1 grey--text text--darken-1">
            ข้อมูลสรุปและจัดการสถานะการย้ายประจำรอบ
            {{ periods.period_times }}/{{ periods.period_year }}
          </div>
        </v-col>

        <!-- ===== Summary Cards Row 1: สถานะการย้าย ===== -->
        <v-col cols="12" sm="6" md="3">
          <v-card
            class="rounded-lg pa-4"
            elevation="2"
            style="border-left: 5px solid #1976D2"
          >
            <div class="d-flex justify-space-between align-center">
              <div>
                <div class="text-overline grey--text">รายการทั้งหมด</div>
                <div class="text-h4 font-weight-bold">{{ stats.total }}</div>
              </div>
              <v-avatar color="blue lighten-5" size="60">
                <v-icon color="blue" x-large>mdi-file-document-multiple</v-icon>
              </v-avatar>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card
            class="rounded-lg pa-4"
            elevation="2"
            style="border-left: 5px solid #4CAF50"
          >
            <div class="d-flex justify-space-between align-center">
              <div>
                <div class="text-overline grey--text">ย้ายปกติ (Agree)</div>
                <div class="text-h4 font-weight-bold success--text">
                  {{ stats.agree }}
                </div>
              </div>
              <v-avatar color="green lighten-5" size="60">
                <v-icon color="success" x-large>mdi-check-circle</v-icon>
              </v-avatar>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card
            class="rounded-lg pa-4"
            elevation="2"
            style="border-left: 5px solid #FF9800"
          >
            <div class="d-flex justify-space-between align-center">
              <div>
                <div class="text-overline grey--text">ตัดโอน (Transfer)</div>
                <div class="text-h4 font-weight-bold warning--text">
                  {{ stats.transfer }}
                </div>
              </div>
              <v-avatar color="orange lighten-5" size="60">
                <v-icon color="warning" x-large>mdi-scissors-cutting</v-icon>
              </v-avatar>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card
            class="rounded-lg pa-4"
            elevation="2"
            style="border-left: 5px solid #00BCD4"
          >
            <div class="d-flex justify-space-between align-center">
              <div>
                <div class="text-overline grey--text">สับเปลี่ยน (Swap)</div>
                <div class="text-h4 font-weight-bold cyan--text">
                  {{ stats.swap }}
                </div>
              </div>
              <v-avatar color="cyan lighten-5" size="60">
                <v-icon color="cyan" x-large>mdi-swap-horizontal-bold</v-icon>
              </v-avatar>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- ===== Validation Panel: ตรวจสอบความถูกต้อง ===== -->
      <v-card
        elevation="2"
        class="rounded-lg mb-4"
        :class="{
          'validation-card-error':
            missingBranchItems.length > 0 || duplicateGroups.length > 0,
          'validation-card-ok':
            missingBranchItems.length === 0 && duplicateGroups.length === 0
        }"
      >
        <v-toolbar flat dense>
          <v-icon
            :color="
              missingBranchItems.length > 0 || duplicateGroups.length > 0
                ? 'red'
                : 'green'
            "
            class="mr-2"
          >
            {{
              missingBranchItems.length > 0 || duplicateGroups.length > 0
                ? "mdi-shield-alert"
                : "mdi-shield-check"
            }}
          </v-icon>
          <v-toolbar-title class="text-subtitle-1 font-weight-bold">
            ผลการตรวจสอบข้อมูล
          </v-toolbar-title>
          <v-spacer></v-spacer>

          <!-- Filter Chips -->
          <v-chip-group v-model="validationFilter" mandatory>
            <v-chip filter outlined small value="all">
              ทั้งหมด ({{ stats.total }})
            </v-chip>
            <v-chip
              filter
              outlined
              small
              value="missing"
              :color="missingBranchItems.length > 0 ? 'red' : 'grey'"
            >
              <v-icon x-small left>mdi-alert-circle</v-icon>
              ไม่มีสาขา ({{ missingBranchItems.length }})
            </v-chip>
            <v-chip
              filter
              outlined
              small
              value="duplicate"
              :color="duplicateGroups.length > 0 ? 'orange' : 'grey'"
            >
              <v-icon x-small left>mdi-content-duplicate</v-icon>
              ซ้ำ ({{ duplicateItemCount }})
            </v-chip>
          </v-chip-group>
        </v-toolbar>

        <!-- Summary Stat Cards -->
        <v-card-text class="pt-2 pb-3">
          <v-row dense>
            <v-col cols="12" sm="4">
              <v-alert
                :type="missingBranchItems.length > 0 ? 'error' : 'success'"
                dense
                text
                class="mb-0"
              >
                <div class="d-flex justify-space-between align-center">
                  <span>
                    <strong>ไม่มีรหัสสาขาวิชา:</strong>
                    {{ missingBranchItems.length }} รายการ
                  </span>
                  <v-btn
                    v-if="missingBranchItems.length > 0"
                    x-small
                    text
                    color="red"
                    @click="validationFilter = 'missing'"
                  >
                    ดูรายการ
                  </v-btn>
                </div>
                <div v-if="missingBranchItems.length > 0" class="caption mt-1">
                  อาจเกิดจากทำรายการย้ายผิดพลาด ควรตรวจสอบและแก้ไข
                </div>
              </v-alert>
            </v-col>

            <v-col cols="12" sm="4">
              <v-alert
                :type="duplicateGroups.length > 0 ? 'warning' : 'success'"
                dense
                text
                class="mb-0"
              >
                <div class="d-flex justify-space-between align-center">
                  <span>
                    <strong>วิทยาลัย+สาขา ซ้ำ:</strong>
                    {{ duplicateGroups.length }} กลุ่ม ({{
                      duplicateItemCount
                    }}
                    รายการ)
                  </span>
                  <v-btn
                    v-if="duplicateGroups.length > 0"
                    x-small
                    text
                    color="orange darken-2"
                    @click="validationFilter = 'duplicate'"
                  >
                    ดูรายการ
                  </v-btn>
                </div>
                <div v-if="duplicateGroups.length > 0" class="caption mt-1">
                  พบรายการที่ย้ายไปวิทยาลัยและสาขาวิชาเดียวกัน
                </div>
              </v-alert>
            </v-col>

            <v-col cols="12" sm="4">
              <v-alert type="info" dense text class="mb-0">
                <strong>ปกติ (ผ่านตรวจสอบ):</strong>
                {{ cleanItemCount }} รายการ
              </v-alert>
            </v-col>
          </v-row>

          <!-- Duplicate Groups Detail -->
          <v-expand-transition>
            <div
              v-if="
                validationFilter === 'duplicate' && duplicateGroups.length > 0
              "
              class="mt-3"
            >
              <div
                class="text-subtitle-2 font-weight-bold mb-2 grey--text text--darken-2"
              >
                <v-icon small color="orange" class="mr-1">mdi-group</v-icon>
                กลุ่มที่ซ้ำ (วิทยาลัย + สาขาวิชา เดียวกัน)
              </div>
              <v-row dense>
                <v-col
                  v-for="(group, idx) in duplicateGroups"
                  :key="idx"
                  cols="12"
                  md="6"
                  lg="4"
                >
                  <v-card outlined class="pa-3 rounded-lg duplicate-group-card">
                    <div class="d-flex align-center mb-2">
                      <v-chip small color="orange" dark label class="mr-2">
                        ซ้ำ {{ group.items.length }} รายการ
                      </v-chip>
                      <span class="caption grey--text"
                        >กลุ่มที่ {{ idx + 1 }}</span
                      >
                    </div>
                    <div class="text-body-2 mb-1">
                      <v-icon x-small color="grey" class="mr-1"
                        >mdi-school</v-icon
                      >
                      <strong>{{ group.college }}</strong>
                    </div>
                    <div class="text-body-2 grey--text text--darken-1 mb-2">
                      <v-icon x-small color="grey" class="mr-1">mdi-tag</v-icon>
                      สาขา:
                      {{ group.branchName || group.branch || "(ไม่ระบุ)" }}
                    </div>
                    <v-divider class="mb-2"></v-divider>
                    <div
                      v-for="person in group.items"
                      :key="person.ctsid_ref"
                      class="text-caption grey--text text--darken-2 mb-1"
                    >
                      <v-icon x-small class="mr-1">mdi-account</v-icon>
                      {{ person.title_s }}{{ person.frist_name }}
                      {{ person.last_name }}
                      <span class="grey--text text--lighten-1"
                        >— จาก {{ person.college_name_old }}</span
                      >
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </div>
          </v-expand-transition>
        </v-card-text>
      </v-card>

      <!-- ===== Toolbar: ค้นหา / Export / Update ===== -->
      <v-card elevation="2" class="rounded-lg mb-4">
        <v-toolbar flat color="white" class="pt-2">
          <v-row align="center">
            <v-col cols="12" md="4">
              <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                label="ค้นหา ชื่อ, วิทยาลัย, หรือรหัส..."
                single-line
                hide-details
                outlined
                dense
                rounded
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="2">
              <v-btn
                color="primary"
                block
                rounded
                depressed
                @click="searchTimeYear"
              >
                <v-icon left>mdi-filter</v-icon> ค้นหา
              </v-btn>
            </v-col>
            <v-col cols="12" md="2">
              <v-btn
                color="success"
                outlined
                block
                rounded
                @click="exportToExcel"
              >
                <v-icon left>mdi-file-excel</v-icon> Export
              </v-btn>
            </v-col>

            <v-col cols="12" md="2">
              <v-btn
                color="success"
                outlined
                block
                rounded
                @click="updateData()"
              >
                <v-icon left>mdi-update</v-icon> Update Data
              </v-btn>
            </v-col>
          </v-row>
        </v-toolbar>

        <v-expansion-panels flat accordion class="mt-2">
          <v-expansion-panel>
            <v-expansion-panel-header class="grey lighten-4">
              <span class="text-subtitle-2 grey--text text--darken-2">
                <v-icon small class="mr-2">mdi-table-cog</v-icon>
                ปรับแต่งคอลัมน์ตาราง
              </span>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="pt-4">
              <v-select
                v-model="selectedHeaders"
                :items="headersMap"
                label="เลือกคอลัมน์ที่ต้องการแสดง"
                multiple
                outlined
                dense
                chips
                small-chips
                deletable-chips
                return-object
              ></v-select>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card>

      <!-- ===== Data Table ===== -->
      <v-card elevation="2" class="rounded-lg">
        <v-data-table
          :headers="showHeaders"
          :items="filteredItems"
          :search="search"
          :loading="loading"
          :item-class="row_classes"
          class="elevation-0"
          :items-per-page="10"
        >
          <!-- Validation Status Column -->
          <template v-slot:item.validation_status="{ item }">           
            <div class="d-flex align-center justify-center">
              <v-tooltip bottom v-if="isMissingBranch(item)">
                <template v-slot:activator="{ on }">
                  <v-chip v-on="on" color="red" dark x-small label class="mr-1">
                    <v-icon x-small left>mdi-alert</v-icon>ไม่มีสาขา
                  </v-chip>
                </template>
                <span>ไม่พบรหัสสาขาวิชา — อาจทำรายการย้ายผิดพลาด</span>
              </v-tooltip>
              <v-tooltip bottom v-if="isDuplicate(item)">
                <template v-slot:activator="{ on }">
                  <v-chip v-on="on" color="orange" dark x-small label>
                    <v-icon x-small left>mdi-content-duplicate</v-icon>ซ้ำ #{{
                      getDuplicateGroupIndex(item) + 1
                    }}
                  </v-chip>
                </template>
                <span>วิทยาลัย + สาขาวิชา ซ้ำกับรายการอื่น</span>
              </v-tooltip>
              <v-icon
                v-if="!isMissingBranch(item) && !isDuplicate(item)"
                color="green"
                small
                >mdi-check-circle</v-icon
              >
            </div>
          </template>

          <template v-slot:item.ctsstatus_select="{ item }">
            <v-chip
              :color="getStatusColor(item.ctsstatus_select)"
              dark
              small
              label
            >
              {{ getStatusText(item.ctsstatus_select) }}
            </v-chip>
          </template>

          <!-- Branch Column: Highlight if missing -->
          <template v-slot:item.ctsid_branch="{ item }">
            <span v-if="item.ctsid_branch">{{ item.ctsid_branch }}</span>
            <v-chip
              v-else
              color="red lighten-4"
              text-color="red darken-3"
              x-small
              label
            >
              <v-icon x-small left>mdi-close-circle</v-icon>ว่าง
            </v-chip>
          </template>

          <template v-slot:item.college_name_new="{ item }">
            <div class="d-flex align-center">
              <v-icon color="success" small class="mr-1"
                >mdi-map-marker-right</v-icon
              >
              <span class="success--text font-weight-bold">{{
                item.college_name_new
              }}</span>
              <v-chip
                v-if="isDuplicate(item)"
                x-small
                color="orange lighten-4"
                text-color="orange darken-3"
                class="ml-1"
              >
                กลุ่ม {{ getDuplicateGroupIndex(item) + 1 }}
              </v-chip>
            </div>
          </template>

          <template v-slot:item.college_name_old="{ item }">
            <div class="grey--text text--darken-2">
              {{ item.college_name_old }}
            </div>
          </template>

          <template v-slot:item.actionCencel="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="red lighten-1"
                  v-bind="attrs"
                  v-on="on"
                  @click="
                    item.ctsstatus_select === 'transfer'
                      ? CencelMovementCut(item.ctsid_ref)
                      : CencelMovement(item.ctsid_ref)
                  "
                >
                  <v-icon>mdi-trash-can-outline</v-icon>
                </v-btn>
              </template>
              <span>ยกเลิก/ลบรายการนี้</span>
            </v-tooltip>
          </template>

          <template v-slot:item.id_branch="{ item }">
            <v-icon v-if="item.id_branch" color="success" small
              >mdi-check-circle</v-icon
            >
          </template>
          <template v-slot:item.service_status="{ item }">
            <v-icon
              v-if="item.service_status === 'service'"
              color="warning"
              small
              >mdi-star</v-icon
            >
          </template>
          <template v-slot:item.demand_college="{ item }">
            <v-icon
              v-if="item.ctsstatus_select === 'demand'"
              color="primary"
              small
              >mdi-hand-pointing-right</v-icon
            >
          </template>
        </v-data-table>
      </v-card>

      <!-- ===== Cancel Dialog ===== -->
      <v-dialog v-model="canceldialog" persistent max-width="700px">
        <v-card class="rounded-xl overflow-hidden">
          <v-toolbar color="red lighten-1" dark flat>
            <v-icon class="mr-2">mdi-alert-circle</v-icon> ยืนยันการยกเลิกรายการ
            <v-spacer></v-spacer>
            <v-btn icon @click="canceldialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text class="pt-6">
            <v-form ref="cancelform" lazy-validation>
              <v-alert border="left" colored-border type="error" elevation="2">
                คุณกำลังจะลบข้อมูลผลสำเร็จของการย้าย โปรดตรวจสอบความถูกต้อง
              </v-alert>

              <v-card outlined class="pa-4 mt-3">
                <div class="text-h6 mb-2">ข้อมูลบุคลากร</div>
                <v-simple-table dense>
                  <tbody>
                    <tr>
                      <td class="font-weight-bold grey--text">ชื่อ-สกุล</td>
                      <td>
                        {{ transference_personnels_id_ref.title_s
                        }}{{ transference_personnels_id_ref.frist_name }}
                        {{ transference_personnels_id_ref.last_name }}
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold grey--text">
                        รหัสบัตรประชาชน
                      </td>
                      <td>{{ transference_personnels_id_ref.id_card }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold grey--text">สถานศึกษาเดิม</td>
                      <td>
                        {{ transference_personnels_id_ref.college_name }} ({{
                          transference_personnels_id_ref.id_position
                        }})
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>

                <v-divider class="my-3"></v-divider>

                <div class="text-h6 mb-2">ข้อมูลปลายทาง</div>
                <div
                  v-if="
                    conditons_transfer_successsSelect.ctsstatus_select ===
                      'transfer'
                  "
                >
                  <v-chip color="orange" dark label small class="mb-2"
                    >ตัดโอนตำแหน่ง</v-chip
                  >
                  <div class="text-body-1">
                    ไปยัง:
                    <strong>{{
                      conditons_transfer_successsSelect.college_name_new
                    }}</strong>
                  </div>
                </div>
                <div v-else>
                  <v-chip color="green" dark label small class="mb-2"
                    >ย้ายปกติ/สับเปลี่ยน</v-chip
                  >
                  <div class="text-body-1">
                    วิทยาลัย:
                    <strong>{{ man_powerss.college_name || "ไม่ระบุ" }}</strong>
                  </div>
                  <div class="text-body-1">
                    เลขที่ตำแหน่ง:
                    <strong>{{ man_powerss.id_position || "ไม่ระบุ" }}</strong>
                  </div>
                </div>
              </v-card>
            </v-form>
          </v-card-text>

          <v-card-actions class="grey lighten-5 pa-4">
            <v-spacer></v-spacer>
            <v-btn text color="grey darken-1" @click="canceldialog = false"
              >ยกเลิก</v-btn
            >
            <v-btn
              color="red"
              dark
              elevation="2"
              @click="
                conditons_transfer_successsSelect.ctsstatus_select ===
                'transfer'
                  ? cancelCutSubmit()
                  : cancelSubmit()
              "
            >
              <v-icon left>mdi-delete</v-icon> ยืนยันการลบ
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>

    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      top
      right
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2" dark>{{ snackbar.icon }}</v-icon>
        <span class="white--text">{{ snackbar.text }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text dark v-bind="attrs" @click="snackbar.show = false"
          >ปิด</v-btn
        >
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Movementmenu from "../../components/admin/movementmenu.vue";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

export default {
  name: "ConditionsTransferSuccess",
  components: { Movementmenu },
  data() {
    return {
      ApiKey: "HRvec2021",
      loading: false,
      search: "",

      // Selectors
      times_select: "",
      years_select: "",
      time_ss: [1, 2],
      year_ss: [2565, 2566, 2567, 2568, 2569, 2570],

      // Validation Filter
      validationFilter: "all",

      // Dialogs
      canceldialog: false,

      // Data
      conditons_transfer_successs: [],
      periods: {},
      transference_personnels_id_ref: {},
      conditons_transfer_successsSelect: {},
      man_powerss: {},
      man_powerCan: {},
      man_power_cancel: {},

      // Table Config — เพิ่มคอลัมน์ "ตรวจสอบ" เป็นคอลัมน์แรก
      selectedHeaders: [],
      headersMap: [
        {
          text: "ตรวจสอบ",
          align: "center",
          value: "validation_status",
          width: "140px",
          sortable: false
        },
        {
          text: "จัดการ",
          align: "center",
          value: "actionCencel",
          width: "80px"
        },
        { text: "สถานะ", align: "center", value: "ctsstatus_select" },
        { text: "ครั้งที่", align: "center", value: "ctstime_s" },
        { text: "ปีที่", align: "center", value: "ctsyear_s" },
        { text: "สังกัดใหม่", align: "left", value: "college_name_new" },
        { text: "จังหวัดใหม่", align: "left", value: "province_name_new" },
        { text: "เลขที่ตำแหน่งใหม่", align: "center", value: "ctsid_position" },
        { text: "สาขาวิชา", align: "center", value: "ctsid_branch" },
        {
          text: "ชื่อ",
          align: "left",
          value: "frist_name",
          sortable: false
        },
         {
          text: "นามสกุล",
          align: "left",
          value: "last_name",
          sortable: false
        },
        { text: "สังกัดเดิม", align: "left", value: "college_name_old" },
        { text: "จังหวัดเดิม", align: "left", value: "province_name_old" },
        { text: "เลขที่เดิม", align: "left", value: "ctsid_postion_old" },
        { text: "คะแนน สอจ.", align: "left", value: "tpvecprovince_score" },
        { text: "ช่วยราชการ", align: "center", value: "service_status" },
        { text: "เงื่อนไข", align: "center", value: "branch_name" },
        { text: "ID Card", align: "center", value: "ctsid_card" }
      ],

      snackbar: {
        show: false,
        color: "",
        timeout: 4000,
        icon: "",
        text: ""
      }
    };
  },

  computed: {
    showHeaders() {
      return this.headersMap.filter(h => this.selectedHeaders.includes(h));
    },

    stats() {
      const total = this.conditons_transfer_successs.length;
      const agree = this.conditons_transfer_successs.filter(
        i => i.ctsstatus_select === "agree"
      ).length;
      const transfer = this.conditons_transfer_successs.filter(
        i => i.ctsstatus_select === "transfer"
      ).length;
      const swap = this.conditons_transfer_successs.filter(
        i =>
          i.ctsstatus_select === "sw_normal" ||
          i.ctsstatus_select === "sw_agree"
      ).length;
      return { total, agree, transfer, swap };
    },

    // ==========================================
    // VALIDATION: ไม่มีรหัสสาขาวิชา
    // ==========================================
    missingBranchItems() {
      return this.conditons_transfer_successs.filter(
        item => !item.ctsid_branch || item.ctsid_branch.toString().trim() === ""
      );
    },

    // ==========================================
    // VALIDATION: ซ้ำ วิทยาลัย + สาขาวิชา
    // ==========================================
    duplicateMap() {
      // สร้าง Map: key = "college_name_new|||ctsid_branch" → [items]
      const countMap = {};
      this.conditons_transfer_successs.forEach(item => {
        if (item.college_name_new && item.ctsid_branch) {
          const key = `${item.college_name_new}|||${item.ctsid_branch}`;
          if (!countMap[key]) countMap[key] = [];
          countMap[key].push(item);
        }
      });
      // เก็บเฉพาะกลุ่มที่มี > 1 รายการ
      const dupMap = {};
      Object.entries(countMap).forEach(([key, items]) => {
        if (items.length > 1) dupMap[key] = items;
      });
      return dupMap;
    },

    duplicateGroups() {
      return Object.entries(this.duplicateMap).map(([key, items]) => {
        const [college, branch] = key.split("|||");
        return {
          college,
          branch,
          branchName: items[0].branch_name || "",
          items
        };
      });
    },

    // Set ของ ctsid_ref ที่เป็นรายการซ้ำ (สำหรับ lookup เร็ว)
    duplicateRefSet() {
      const refSet = new Set();
      Object.values(this.duplicateMap).forEach(items => {
        items.forEach(item => refSet.add(item.ctsid_ref));
      });
      return refSet;
    },

    duplicateItemCount() {
      return this.duplicateRefSet.size;
    },

    cleanItemCount() {
      return this.conditons_transfer_successs.filter(
        item =>
          item.ctsid_branch &&
          item.ctsid_branch.toString().trim() !== "" &&
          !this.duplicateRefSet.has(item.ctsid_ref)
      ).length;
    },

    // ==========================================
    // FILTERED ITEMS: ตามปุ่มกรอง
    // ==========================================
    filteredItems() {
      if (this.validationFilter === "missing") {
        return this.missingBranchItems;
      }
      if (this.validationFilter === "duplicate") {
        return this.conditons_transfer_successs.filter(item =>
          this.duplicateRefSet.has(item.ctsid_ref)
        );
      }
      return this.conditons_transfer_successs;
    }
  },

  created() {
    // Default headers — เพิ่ม validation_status
    const defaultKeys = [
      "validation_status",
      "actionCencel",
      "ctsstatus_select",
      "college_name_new",
      "ctsid_position",
      "ctsid_branch",
      "frist_name",
      "last_name",
      "college_name_old",
      "ctsid_postion_old",
      "tpvecprovince_score"
    ];
    this.selectedHeaders = this.headersMap.filter(h =>
      defaultKeys.includes(h.value)
    );
  },

  async mounted() {
    await this.periodQuery();
    await this.conditons_transfer_successQueryAll();
  },

  methods: {
    // ==========================================
    // VALIDATION HELPERS
    // ==========================================
    isMissingBranch(item) {
      return !item.ctsid_branch || item.ctsid_branch.toString().trim() === "";
    },

    isDuplicate(item) {
      return this.duplicateRefSet.has(item.ctsid_ref);
    },

    getDuplicateGroupIndex(item) {
      if (!item.college_name_new || !item.ctsid_branch) return -1;
      const key = `${item.college_name_new}|||${item.ctsid_branch}`;
      return Object.keys(this.duplicateMap).indexOf(key);
    },

    row_classes(item) {
      if (this.isMissingBranch(item)) return "missing-branch-row";
      if (this.isDuplicate(item)) return "duplicate-row";
      return "";
    },

    // ==========================================
    // DATA FETCHING (เดิม)
    // ==========================================
    async updateData() {
      this.loading = true;
      let result = await this.$http.post(
        "conditions_transfer_success_update.php",
        { ApiKey: this.ApiKey }
      );
      if (result.data.status == true) {
        this.showSnackbar("success", "ปรับปรุงข้อมูลเรียบร้อย");
        await this.conditons_transfer_successQueryAll();
      }
      this.loading = false;
    },

    async periodQuery() {
      try {
        let result = await this.$http.post("period.php", {
          ApiKey: this.ApiKey,
          period_id: "301"
        });
        this.periods = result.data;
        if (this.periods.period_times)
          this.times_select = this.periods.period_times;
        if (this.periods.period_year)
          this.years_select = this.periods.period_year;
      } catch (e) {
        console.error("Period Error", e);
      }
    },

    async conditons_transfer_successQueryAll() {
      this.loading = true;
      try {
        let result = await this.$http.post("conditons_transfer_success.php", {
          ApiKey: this.ApiKey,
          ctstime_s: this.periods.period_times,
          ctsyear_s: this.periods.period_year
        });
        this.conditons_transfer_successs = result.data;

        // แจ้งเตือนอัตโนมัติหลังโหลดข้อมูล
        this.$nextTick(() => {
          if (this.missingBranchItems.length > 0) {
            this.showSnackbar(
              "error",
              `พบ ${this.missingBranchItems.length} รายการที่ไม่มีรหัสสาขาวิชา (อาจย้ายผิดพลาด)`
            );
          } else if (this.duplicateGroups.length > 0) {
            this.showSnackbar(
              "warning",
              `พบ ${this.duplicateGroups.length} กลุ่มที่วิทยาลัย+สาขาซ้ำกัน`
            );
          }
        });
      } catch (e) {
        this.showSnackbar("error", "โหลดข้อมูลไม่สำเร็จ");
      } finally {
        this.loading = false;
      }
    },

    async searchTimeYear() {
      this.loading = true;
      try {
        let result = await this.$http.post("conditons_transfer_success.php", {
          ApiKey: this.ApiKey,
          ctstime_s: this.times_select,
          ctsyear_s: this.years_select
        });
        this.conditons_transfer_successs = result.data;
        this.showSnackbar("success", `พบข้อมูล ${result.data.length} รายการ`);
      } catch (e) {
        this.showSnackbar("error", "ค้นหาล้มเหลว");
      } finally {
        this.loading = false;
      }
    },

    // ==========================================
    // CANCEL ACTIONS (เดิม)
    // ==========================================
    async CencelMovement(id_ref) {
      this.loading = true;
      try {
        let res_person = await this.$http.post("transference_personnel.php", {
          ApiKey: this.ApiKey,
          id_ref: id_ref
        });
        this.transference_personnels_id_ref = res_person.data;

        let res_man = await this.$http.post("man_power.php", {
          ApiKey: this.ApiKey,
          id_card: this.transference_personnels_id_ref.id_card
        });
        this.man_powerss = res_man.data;

        let res_cts = await this.$http.post("conditons_transfer_success.php", {
          ApiKey: this.ApiKey,
          ctsid_ref: id_ref
        });
        this.conditons_transfer_successsSelect = res_cts.data;
        this.canceldialog = true;
      } catch (e) {
        this.showSnackbar("error", "ดึงข้อมูลเพื่อยกเลิกไม่สำเร็จ");
      } finally {
        this.loading = false;
      }
    },

    async cancelSubmit() {
      try {
        this.man_powerCan.ApiKey = this.ApiKey;
        this.man_powerCan.status_booking = "";
        this.man_powerCan.id_position = this.man_powerss.id_position;

        this.man_power_cancel.ApiKey = this.ApiKey;
        this.man_power_cancel.id_position = this.conditons_transfer_successsSelect.ctsid_postion_old;

        if (this.man_powerss) {
          await this.$http.post(
            "man_power.update_process.php",
            this.man_powerCan
          );
        }

        let res_man_del = await this.$http.post(
          "man_power.delete.php",
          this.man_power_cancel
        );

        this.conditons_transfer_successsSelect.ApiKey = this.ApiKey;
        let res_cts = await this.$http.post(
          "conditons_transfer_success.delete.php",
          this.conditons_transfer_successsSelect
        );

        if (res_cts.data.status == true && res_man_del.data.status == true) {
          this.showSnackbar("success", "ยกเลิกและคืนสถานะเรียบร้อย");
          this.canceldialog = false;
          this.searchTimeYear();
        } else {
          this.showSnackbar("error", "เกิดข้อผิดพลาดในการบันทึก");
        }
      } catch (e) {
        this.showSnackbar("error", "Server Error");
      }
    },

    async CencelMovementCut(id_ref) {
      this.loading = true;
      try {
        let res_person = await this.$http.post("transference_personnel.php", {
          ApiKey: this.ApiKey,
          id_ref: id_ref
        });
        this.transference_personnels_id_ref = res_person.data;

        let res_cts = await this.$http.post("conditons_transfer_success.php", {
          ApiKey: this.ApiKey,
          ctsid_ref: id_ref
        });
        this.conditons_transfer_successsSelect = res_cts.data;
        this.canceldialog = true;
      } catch (e) {
        this.showSnackbar("error", "ดึงข้อมูลไม่สำเร็จ");
      } finally {
        this.loading = false;
      }
    },

    async cancelCutSubmit() {
      try {
        this.conditons_transfer_successsSelect.ApiKey = this.ApiKey;
        let result = await this.$http.post(
          "conditons_transfer_success.delete.php",
          this.conditons_transfer_successsSelect
        );

        if (result.data.status == true) {
          this.showSnackbar("success", "ยกเลิกรายการตัดโอนเรียบร้อย");
          this.canceldialog = false;
          this.searchTimeYear();
        } else {
          this.showSnackbar("error", "ลบข้อมูลล้มเหลว");
        }
      } catch (e) {
        this.showSnackbar("error", "Server Error");
      }
    },

    // ==========================================
    // UTILITIES (เดิม + เพิ่ม validation ใน export)
    // ==========================================
    exportToExcel() {
      const exportData = this.conditons_transfer_successs.map(item => ({
        ตรวจสอบ: this.isMissingBranch(item)
          ? "ไม่มีสาขา"
          : this.isDuplicate(item)
          ? `ซ้ำ กลุ่ม ${this.getDuplicateGroupIndex(item) + 1}`
          : "ปกติ",
        ประเภท: this.getStatusText(item.ctsstatus_select),
        ครั้งที่: item.ctstime_s,
        ปีที่: item.ctsyear_s,
        "ชื่อ-สกุล": `${item.title_s}${item.frist_name} ${item.last_name}`,
        รหัสบัตรประชาชน: item.ctsid_card,
        สังกัดเดิม: item.college_name_old,
        จังหวัดเดิม: item.province_name_old,
        เลขที่ตำแหน่งเดิม: item.ctsid_postion_old,
        สังกัดใหม่: item.college_name_new,
        จังหวัดใหม่: item.province_name_new,
        เลขที่ตำแหน่งใหม่: item.ctsid_position,
        รหัสสาขา: item.ctsid_branch || "",
        สาขา: item.branch_name
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Transfer_Success");
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array"
      });
      const data = new Blob([excelBuffer], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8"
      });
      saveAs(
        data,
        `Transfer_Result_${this.times_select}_${this.years_select}.xlsx`
      );
    },

    getStatusColor(status) {
      if (status === "transfer") return "orange";
      if (status === "agree") return "success";
      if (status === "demand") return "purple";
      if (status === "sw_normal" || status === "sw_agree") return "cyan";
      return "grey";
    },

    getStatusText(status) {
      if (status === "transfer") return "ตัดโอน";
      if (status === "agree") return "ย้ายปกติ";
      if (status === "demand") return "ประสงค์รับ";
      if (status === "sw_normal") return "สับเปลี่ยน (ปกติ)";
      if (status === "sw_agree") return "สับเปลี่ยน (แลกเปลี่ยน)";
      return status;
    },

    showSnackbar(color, text) {
      this.snackbar.color = color;
      this.snackbar.text = text;
      this.snackbar.icon =
        color === "success" ? "mdi-check-circle" : "mdi-alert-circle";
      this.snackbar.show = true;
    }
  }
};
</script>

<style scoped>
/* ===== Validation Card Borders ===== */
.validation-card-error {
  border-top: 3px solid #f44336;
}
.validation-card-ok {
  border-top: 3px solid #4caf50;
}

/* ===== Row Highlights ===== */
.missing-branch-row {
  background: linear-gradient(
    90deg,
    rgba(244, 67, 54, 0.06) 0%,
    rgba(244, 67, 54, 0.01) 100%
  ) !important;
  border-left: 3px solid #f44336;
}

.duplicate-row {
  background: linear-gradient(
    90deg,
    rgba(255, 152, 0, 0.06) 0%,
    rgba(255, 152, 0, 0.01) 100%
  ) !important;
  border-left: 3px solid #ff9800;
}

/* ===== Duplicate Group Card ===== */
.duplicate-group-card {
  transition: all 0.2s ease;
}
.duplicate-group-card:hover {
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.2) !important;
}
</style>
